"use client";

import { useState, useMemo, useEffect } from "react";
import { useApp } from "@/context/AppContext";
import { useVehicles } from "@/context/VehicleContext";
import { Plus } from "lucide-react";
import { Vehicle, VehicleType } from "@/types";
import { toast } from "sonner";
import CommonHeader from "./common/CommonHeader";
import { VehicleSearchBar } from "./vehicle/VehicleSearchBar";
import { VehiclesTable } from "./vehicle/VehiclesTable";
import {
  VehicleFormDialog,
  VehicleFormData,
} from "./vehicle/VehicleFormDialog";
import { VehicleDetailDialog } from "./vehicle/VehicleDetailDialog";
import { VehicleDeleteDialog } from "./vehicle/VehicleDeleteDialog";
import { vehicleService } from "@/services/vehicle/vehicleService";
import { userService } from "@/services/userService";
import { makeService } from "@/services/vehicle/makeService";
import { modelService } from "@/services/vehicle/modelService";
import {
  CustomerWithDetails,
  Customer,
  VehicleMakeOutput,
  VehicleModelOutput,
} from "@/types";

export function VehicleManagement() {
  const {
    vehicles,
    customers,
    addVehicle,
    updateVehicle,
    deleteVehicle,
    appointments,
  } = useApp();
  const { refreshVehicles } = useVehicles();

  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState<{
    key: keyof Vehicle;
    direction: "asc" | "desc";
  }>({
    key: "plateNumber",
    direction: "asc",
  });
  const [fetchedCustomers, setFetchedCustomers] = useState<Customer[]>([]);
  const [makes, setMakes] = useState<VehicleMakeOutput[]>([]);
  const [models, setModels] = useState<VehicleModelOutput[]>([]);

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);

  const [formData, setFormData] = useState<VehicleFormData>({
    customerId: "",
    plateNumber: "",
    makeId: "",
    modelId: "",
    year: new Date().getFullYear(),
    color: "",
    type: "sedan" as VehicleType,
    notes: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch customers, makes, and models from API on component mount
  // Vehicles are fetched by VehicleContext
  useEffect(() => {
    fetchCustomers();
    fetchMakes();
    fetchModels();
  }, []);

  const fetchCustomers = async () => {
    try {
      const data = await userService.getAllCustomers();
      // Map CustomerWithDetails to Customer format for the dialog
      const mappedCustomers: Customer[] = data.map(
        (customer: CustomerWithDetails) => ({
          id: customer.id,
          name: customer.name,
          phone: customer.phone,
          email: customer.email,
          address: customer.address || undefined,
          createdAt: new Date(customer.createdAt),
          totalSpending: 0,
          customerType: "offline-customer" as const,
          customerCategory: "occasional-customer" as const,
          customerStatus: "active" as const,
          numberOfInteriorCleaning: 0,
          serviceProviders: [],
        }),
      );
      setFetchedCustomers(mappedCustomers);
    } catch (err) {
      console.error("Error fetching customers:", err);
      toast.error("Failed to load customers. Please try again.");
    }
  };

  const fetchMakes = async () => {
    try {
      const data = await makeService.getAllMakes();
      // console.log(data);
      setMakes(data);
    } catch (err) {
      console.error("Error fetching makes:", err);
      toast.error("Failed to load vehicle makes. Please try again.");
    }
  };

  const fetchModels = async () => {
    try {
      const data = await modelService.getAllModels();
      setModels(data);
    } catch (err) {
      console.error("Error fetching models:", err);
      toast.error("Failed to load vehicle models. Please try again.");
    }
  };

  const handleMakeCreated = async (makeId: string) => {
    await fetchMakes();
    // The newly created make will be in the updated makes list
    // The VehicleFormDialog will handle auto-selection
  };

  const handleModelCreated = async (modelId: string) => {
    await fetchModels();
    // The newly created model will be in the updated models list
    // The VehicleFormDialog will handle auto-selection
  };

  // Filtered and sorted vehicles
  const filteredVehicles = useMemo(() => {
    let filtered = vehicles.filter((vehicle) => {
      const customer = customers.find((c) => c.id === vehicle.customerId);
      return (
        vehicle.plateNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
        vehicle.make.toLowerCase().includes(searchTerm.toLowerCase()) ||
        vehicle.model.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer?.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    });

    filtered.sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (
        aValue === undefined ||
        aValue === null ||
        bValue === undefined ||
        bValue === null
      )
        return 0;

      if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [vehicles, customers, searchTerm, sortConfig]);

  const handleSort = (key: keyof Vehicle) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const resetForm = () => {
    setFormData({
      customerId: "",
      plateNumber: "",
      makeId: "",
      modelId: "",
      year: new Date().getFullYear(),
      color: "",
      type: "sedan",
      notes: "",
    });
  };

  // Map VehicleType to API format
  const mapVehicleTypeToAPI = (type: VehicleType): string => {
    const typeMap: Record<VehicleType, string> = {
      sedan: "Sedan",
      suv: "SUV",
      truck: "Truck",
      van: "Van",
    };
    return typeMap[type];
  };

  const handleAdd = async () => {
    try {
      if (
        !formData.customerId ||
        !formData.plateNumber ||
        !formData.makeId ||
        !formData.modelId ||
        !formData.color
      ) {
        toast.error("Please fill in all required fields");
        return;
      }

      setIsSubmitting(true);

      // Use context's addVehicle method which handles API call and state update
      await addVehicle({
        customerId: formData.customerId,
        plateNumber: formData.plateNumber,
        color: formData.color,
        type: formData.type,
        makeId: formData.makeId,
        modelId: formData.modelId,
        year: formData.year,
        notes: formData.notes || undefined,
      });

      // Refresh vehicles explicitly to ensure table updates
      await refreshVehicles();

      // Close dialog and reset form after successful creation
      setIsAddDialogOpen(false);
      resetForm();
    } catch (err: any) {
      console.error("Error adding vehicle:", err);
      // Error toast is already handled in the context's addVehicle method
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = async () => {
    if (
      !selectedVehicle ||
      !formData.plateNumber ||
      !formData.makeId ||
      !formData.modelId ||
      !formData.color
    ) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      setIsSubmitting(true);

      // Transform form data to API payload format
      const payload = {
        type: mapVehicleTypeToAPI(formData.type),
        plateNumber: formData.plateNumber,
        color: formData.color,
        notes: formData.notes || null,
        makeId: formData.makeId,
        modelId: formData.modelId,
        year: formData.year,
      };

      await vehicleService.updateVehicle(selectedVehicle.id, payload);

      // Update context state with the changes
      updateVehicle(selectedVehicle.id, {
        plateNumber: formData.plateNumber,
        color: formData.color,
        type: formData.type,
        makeId: formData.makeId,
        modelId: formData.modelId,
        year: formData.year,
        notes: formData.notes || undefined,
      });

      // Refresh vehicles to get latest data from server
      await refreshVehicles();

      toast.success("Vehicle updated successfully");

      setIsEditDialogOpen(false);
      setSelectedVehicle(null);
      resetForm();
    } catch (err: any) {
      console.error("Error updating vehicle:", err);
      toast.error(
        err?.response?.data?.message ||
          "Failed to update vehicle. Please try again.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedVehicle) return;

    try {
      await vehicleService.deleteVehicle(selectedVehicle.id);

      // Update context state
      deleteVehicle(selectedVehicle.id);

      // Refresh vehicles to ensure state is in sync
      await refreshVehicles();

      toast.success("Vehicle deleted successfully");

      setIsDeleteDialogOpen(false);
      setSelectedVehicle(null);
    } catch (err: any) {
      console.error("Error deleting vehicle:", err);
      toast.error(
        err?.response?.data?.message ||
          "Failed to delete vehicle. Please try again.",
      );
    }
  };

  const openEditDialog = (vehicle: Vehicle) => {
    setSelectedVehicle(vehicle);
    setFormData({
      customerId: vehicle.customerId,
      plateNumber: vehicle.plateNumber,
      makeId: vehicle.makeId || "", // Use stored ID
      modelId: vehicle.modelId || "", // Use stored ID
      year: vehicle.year || new Date().getFullYear(), // Use year from vehicle or default
      color: vehicle.color,
      type: vehicle.type,
      notes: vehicle.notes || "",
    });
    setIsEditDialogOpen(true);
  };

  const openDetailDialog = (vehicle: Vehicle) => {
    setSelectedVehicle(vehicle);
    setIsDetailDialogOpen(true);
  };

  const openDeleteDialog = (vehicle: Vehicle) => {
    setSelectedVehicle(vehicle);
    setIsDeleteDialogOpen(true);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <CommonHeader
        title="Vehicle Management"
        description="Manage customer vehicles"
        onClick={() => setIsAddDialogOpen(true)}
        buttonText="Add Vehicle"
        buttonIcon={Plus}
      />

      {/* Search */}
      <VehicleSearchBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
      />

      {/* Vehicles Table */}
      <VehiclesTable
        vehicles={filteredVehicles}
        customers={customers}
        sortConfig={sortConfig}
        onSort={handleSort}
        onView={openDetailDialog}
        onEdit={openEditDialog}
        onDelete={openDeleteDialog}
      />

      {/* Add Vehicle Dialog */}
      <VehicleFormDialog
        open={isAddDialogOpen}
        onOpenChange={setIsAddDialogOpen}
        formData={formData}
        onFormDataChange={setFormData}
        onSave={handleAdd}
        onCancel={() => {
          setIsAddDialogOpen(false);
          resetForm();
        }}
        mode="add"
        customers={fetchedCustomers}
        makes={makes}
        models={models}
        onMakesRefresh={fetchMakes}
        onModelsRefresh={fetchModels}
      />

      {/* Edit Vehicle Dialog */}
      <VehicleFormDialog
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
        formData={formData}
        onFormDataChange={setFormData}
        onSave={handleEdit}
        onCancel={() => {
          setIsEditDialogOpen(false);
          resetForm();
        }}
        mode="edit"
        customers={fetchedCustomers}
        makes={makes}
        models={models}
        onMakesRefresh={fetchMakes}
        onModelsRefresh={fetchModels}
      />

      {/* Vehicle Detail Dialog */}
      <VehicleDetailDialog
        open={isDetailDialogOpen}
        onOpenChange={setIsDetailDialogOpen}
        vehicle={selectedVehicle}
        customers={customers}
        appointments={appointments}
      />

      {/* Delete Confirmation Dialog */}
      <VehicleDeleteDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        vehicle={selectedVehicle}
        onConfirm={handleDelete}
      />
    </div>
  );
}
